import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  adminAuthService,
  adminDevicesService,
  adminInstancesService,
  adminModelsService,
  adminOpenTofuService,
} from '@/api/services/admin';
import { adminCatalogService } from '@/api/services/adminCatalog';
import { CATALOG_JOB_TERMINAL, useJobPolling } from '@/api/hooks/useJobPolling';
import type {
  BenchmarkStatus,
  CatalogGenerateRequest,
  DeletionReason,
  ModelAdminResponse,
} from '@/api/types';
import type {
  EvalScoreValues,
  ModelCreateValues,
  ModelUpdateValues,
} from '@/features/admin/models/schemas';

// Users
export const adminUsersKey = ['admin', 'users'] as const;

export function useAdminUsers() {
  return useQuery({ queryKey: adminUsersKey, queryFn: adminAuthService.listUsers });
}

export function useUpdateUserStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      adminAuthService.updateStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKey }),
  });
}

export function usePromoteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminAuthService.promote(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKey }),
  });
}

export function useDemoteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminAuthService.demote(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKey }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: DeletionReason }) =>
      adminAuthService.removeUser(id, reason),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKey }),
  });
}

// Instances
export const adminInstancesKey = ['admin', 'instances'] as const;
export const adminInstanceStatsKey = ['admin', 'instances', 'stats'] as const;

export function useAdminInstances() {
  return useQuery({ queryKey: adminInstancesKey, queryFn: adminInstancesService.list });
}

export function useAdminInstanceStats() {
  return useQuery({ queryKey: adminInstanceStatsKey, queryFn: adminInstancesService.stats });
}

export function useUploadInstancesCsv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => adminInstancesService.uploadCsv(file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminInstancesKey });
      qc.invalidateQueries({ queryKey: adminInstanceStatsKey });
    },
  });
}

export function useClearInstances() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adminInstancesService.clearAll(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminInstancesKey });
      qc.invalidateQueries({ queryKey: adminInstanceStatsKey });
    },
  });
}

// Models
// Admin shares the SAME cache key as the public Models page (`['models']`)
// because both views read from `GET /models`. A CSV upload or metadata
// refresh in admin instantly refreshes the public catalog too.
const modelsKey = ['models'] as const;

export function useAdminModels() {
  return useQuery({ queryKey: modelsKey, queryFn: adminModelsService.list });
}

export function useDeleteAdminModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminModelsService.remove(id),
    // Retargeted to invalidate BOTH the public `['models']` list and the new
    // admin catalog list (FE contract §5).
    onSuccess: () => invalidateCatalog(qc),
  });
}

// ====================================================================
// Model catalog management (R3 generation jobs + R5 admin CRUD/lifecycle)
// FE contract §5. Cache-key discipline: catalog-shape mutations invalidate
// BOTH `['models']` (public) and `['admin','models']` (admin list); job /
// history / eval / constraints reads get DEDICATED keys so polling never
// thrashes the public list.
// ====================================================================

export const adminModelsKey = ['admin', 'models'] as const;
export const adminCatalogJobsKey = ['admin', 'catalog', 'jobs'] as const;
export const adminCatalogJobKey = (id: string) => ['admin', 'catalog', 'job', id] as const;
export const adminCatalogConstraintsKey = ['admin', 'catalog', 'constraints'] as const;
export const adminModelHistoryKey = (id: string) => ['admin', 'model', id, 'history'] as const;
export const adminModelEvalKey = (id: string) => ['admin', 'model', id, 'eval-scores'] as const;

/** Every catalog-shape mutation invalidates both the public and admin lists. */
function invalidateCatalog(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: modelsKey }); // public Models page + shared consumers
  qc.invalidateQueries({ queryKey: adminModelsKey }); // admin catalog list
}

// --- Reads ----------------------------------------------------------

/** Backend caps `limit` at 500 (GET /admin/models, ge=1 le=500) — page through
 *  until `total` is reached so the page keeps its client-side facet/sort UX
 *  regardless of catalog size. */
const ADMIN_MODELS_PAGE_SIZE = 500;

/** Flattened catalog plus the envelope-level benchmark availability. The
 *  `benchmark_status` rides on every page; an outage on ANY page marks the
 *  whole result `"unavailable"` (coverage is then HIDDEN, not shown as 0%). */
export type AdminCatalogModels = {
  items: ModelAdminResponse[];
  benchmarkStatus: BenchmarkStatus;
};

async function fetchAllCatalogModels(excludeArchived: boolean): Promise<AdminCatalogModels> {
  const first = await adminModelsService.listCatalog({
    exclude_archived: excludeArchived,
    limit: ADMIN_MODELS_PAGE_SIZE,
    skip: 0,
  });
  const items = [...first.items];
  // Absent → "ok" (older backends). Any page reporting "unavailable" wins.
  let benchmarkStatus: BenchmarkStatus =
    first.benchmark_status === 'unavailable' ? 'unavailable' : 'ok';
  while (items.length < first.total) {
    const page = await adminModelsService.listCatalog({
      exclude_archived: excludeArchived,
      limit: ADMIN_MODELS_PAGE_SIZE,
      skip: items.length,
    });
    if (page.benchmark_status === 'unavailable') benchmarkStatus = 'unavailable';
    if (page.items.length === 0) break; // defensive: avoid a spin if total drifts
    items.push(...page.items);
  }
  return { items, benchmarkStatus };
}

/** Admin catalog list — DEDICATED key; Show-archived toggle flips
 *  `exclude_archived`. */
export function useAdminCatalogModels(includeArchived: boolean) {
  return useQuery({
    queryKey: [...adminModelsKey, { includeArchived }],
    queryFn: () => fetchAllCatalogModels(!includeArchived),
  });
}

export function useCatalogGenerationJob(jobId: string | null) {
  return useJobPolling(
    adminCatalogJobKey(jobId ?? ''),
    () => adminCatalogService.getJob(jobId as string),
    { enabled: !!jobId },
  );
}

/** Job states at which LIST polling stops — terminal states plus
 *  `preview_ready` (a resting state: nothing changes until the admin commits),
 *  mirroring `useJobPolling`'s stop-set. */
const JOB_LIST_RESTING = new Set<string>([...CATALOG_JOB_TERMINAL, 'preview_ready']);

export function useCatalogJobs(
  params?: { job_type?: string; status?: string; limit?: number },
  opts?: { poll?: boolean },
) {
  return useQuery({
    queryKey: [...adminCatalogJobsKey, params ?? {}],
    queryFn: () => adminCatalogService.listJobs(params),
    staleTime: 30_000,
    // Opt-in list polling (jobs tray): refetch every 2.5s while any listed job
    // is still moving; fall back to the default staleTime behaviour once all
    // rows are resting. `refetchInterval` is per-observer, so existing
    // consumers of the same key are unaffected.
    refetchInterval: opts?.poll
      ? (q) => {
          const items = q.state.data?.items ?? [];
          const active = items.some((j) => !JOB_LIST_RESTING.has(j.status?.toLowerCase()));
          return active ? 2500 : false;
        }
      : undefined,
  });
}

/** On-demand bulk-collection trigger (jobs tray owns progress + completion
 *  toasts). 'metadata' -> POST /admin/catalog/enrich; 'eval' ->
 *  POST /admin/catalog/eval-refresh. Invalidates ONLY the jobs list so the
 *  tray picks up the new run immediately; catalog lists refresh when the job
 *  reaches a terminal state (tray effect). */
export type CatalogTriggerKind = 'metadata' | 'eval';

export function useTriggerCatalogJob(kind: CatalogTriggerKind) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      kind === 'metadata' ? adminCatalogService.enrich() : adminCatalogService.evalRefresh(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminCatalogJobsKey });
    },
  });
}

export function useCatalogConstraints(enabled = true) {
  return useQuery({
    queryKey: adminCatalogConstraintsKey,
    queryFn: adminCatalogService.getConstraints,
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useModelHistory(id: string | null, params?: { skip?: number; limit?: number }) {
  return useQuery({
    queryKey: [...adminModelHistoryKey(id ?? ''), params ?? {}],
    queryFn: () => adminModelsService.history(id as string, params),
    enabled: !!id,
    staleTime: 30_000,
  });
}
// useModelEvalScores(id) — pending GET endpoint (FE contract §9 Q3).

// --- Mutations ------------------------------------------------------

/** POST /admin/catalog/generate. Invalidates ONLY the jobs list so the tray
 *  picks up the new generation run immediately (badge + progress) — mirroring
 *  useTriggerCatalogJob. The catalog/models lists are deliberately NOT
 *  invalidated: generation commits nothing, so catalog rows only refresh at
 *  commit (useCommitCatalogJob) or when the run reaches a terminal state
 *  (JobsTray effect). */
export function useGenerateCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CatalogGenerateRequest) => adminCatalogService.generate(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminCatalogJobsKey });
    },
  });
}

export function useCommitCatalogJob(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adminCatalogService.commitJob(jobId),
    onSuccess: () => {
      invalidateCatalog(qc);
      qc.invalidateQueries({ queryKey: adminCatalogJobKey(jobId) });
      qc.invalidateQueries({ queryKey: adminCatalogJobsKey });
    },
  });
}

export function useCancelCatalogJob(jobId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adminCatalogService.cancelJob(jobId),
    // NOT invalidateCatalog — a cancel changes no catalog rows.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminCatalogJobKey(jobId) });
      qc.invalidateQueries({ queryKey: adminCatalogJobsKey });
    },
  });
}

/** POST /admin/models/bulk (dry_run=false) — creates the new identities →
 *  refresh BOTH the public `['models']` list and the admin catalog list. */
export function useBulkAddModels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (identities: string[]) => adminModelsService.bulkAdd(identities, false),
    onSuccess: () => invalidateCatalog(qc),
  });
}

/** POST /admin/models/bulk (dry_run=true) — classification only (new / exists
 *  / invalid). Debounce-friendly: NO cache invalidation (nothing changes). */
export function useValidateBulkModels() {
  return useMutation({
    mutationFn: (identities: string[]) => adminModelsService.bulkAdd(identities, true),
  });
}

export function useCreateModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ModelCreateValues) => adminModelsService.create(body),
    onSuccess: () => invalidateCatalog(qc),
  });
}

export function useUpdateModel(id: string) {
  const qc = useQueryClient();
  return useMutation({
    // Only changed/dirty fields (admin-authored subset) are passed in.
    mutationFn: (body: Partial<ModelUpdateValues>) => adminModelsService.update(id, body),
    onSuccess: () => invalidateCatalog(qc),
  });
}

export type ModelLifecycleAction = 'deprecate' | 'restore' | 'archive' | 'unarchive';

/** One hook for all four lifecycle transitions; the action is passed at call
 *  time and routed to the matching service method. */
export function useModelLifecycle(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (action: ModelLifecycleAction) => adminModelsService[action](id),
    onSuccess: () => invalidateCatalog(qc),
  });
}

/** Per-model synchronous HF metadata refresh (R2 §8). Replaces the retired
 *  fire-and-forget bulk refresh — surfaced per-row via `SyncStatusChip`. */
export function useRefreshMetadataOne(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => adminModelsService.refreshMetadataOne(id),
    // metadata_synced_at + HF fields change the row → refresh both lists.
    onSuccess: () => invalidateCatalog(qc),
  });
}

export function useSetEvalScore(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: EvalScoreValues) => adminModelsService.setEvalScore(id, body),
    // Eval-only key — eval scores don't affect catalog-list rendering.
    onSuccess: () => qc.invalidateQueries({ queryKey: adminModelEvalKey(id) }),
  });
}

// Devices
export const adminDevicesKey = ['admin', 'devices'] as const;
export function useAdminDevices() {
  return useQuery({ queryKey: adminDevicesKey, queryFn: adminDevicesService.list });
}

// OpenTofu cache
export const adminOpenTofuKey = ['admin', 'opentofu-cache'] as const;
export function useAdminOpenTofuList() {
  return useQuery({ queryKey: adminOpenTofuKey, queryFn: () => adminOpenTofuService.list() });
}
export function useAdminOpenTofuStats() {
  return useQuery({
    queryKey: [...adminOpenTofuKey, 'stats'],
    queryFn: adminOpenTofuService.stats,
  });
}
export function useDeleteAdminOpenTofu() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => adminOpenTofuService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminOpenTofuKey }),
  });
}
export function useAdminOpenTofuDetail(id: string | null) {
  return useQuery({
    queryKey: [...adminOpenTofuKey, 'detail', id ?? ''],
    queryFn: () => adminOpenTofuService.detail(id as string),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}
